import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingCart, Zap, CheckCircle2, Leaf, Info, ChevronRight, ShieldCheck, Truck, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ProductCard } from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { PRODUCTS, formatINR } from "@/data/catalog";
import { toast } from "sonner";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const { addItem, buyNow, setCartOpen } = useCart();
  const [imgIndex, setImgIndex] = useState(0);

  if (!product) {
    return (
      <div className="py-24 text-center" data-testid="product-not-found">
        <h1 className="font-display text-3xl text-[#1c2826] mb-4">Product not found</h1>
        <Link to="/products" className="text-[#C05C46] font-semibold hover:underline">
          Browse all products
        </Link>
      </div>
    );
  }

  const hasDiscount = product.mrp > product.price;
  const related = PRODUCTS.filter((p) => p.slug !== slug && p.category === product.category).slice(0, 3);
  const relatedFill = related.length
    ? related
    : PRODUCTS.filter((p) => p.slug !== slug).slice(0, 3);

  const handleAdd = () => {
    addItem(product.slug);
    toast.success(`${product.name} added to cart`);
    setCartOpen(true);
  };

  return (
    <section data-testid="product-detail-page" className="py-10 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav data-testid="breadcrumbs" className="flex items-center gap-1.5 text-xs text-[#4B5563] mb-8">
          <Link to="/" className="hover:text-[#1A4331]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/products" className="hover:text-[#1A4331]">Products</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#1A4331] font-medium">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
            <div className="bg-white rounded-2xl border border-[#E5E0D8] shadow-sm p-6 sm:p-10">
              <div className="aspect-square flex items-center justify-center">
                <img
                  data-testid="detail-image"
                  src={product.gallery[imgIndex] || product.image}
                  alt={product.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
            {product.gallery.length > 1 && (
              <div className="flex gap-3 mt-4 justify-center">
                {product.gallery.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`detail-thumb-${i}`}
                    onClick={() => setImgIndex(i)}
                    className={`w-16 h-16 rounded-xl border-2 overflow-hidden bg-white p-1 ${
                      imgIndex === i ? "border-[#C05C46]" : "border-[#E5E0D8]"
                    }`}
                  >
                    <img src={g} alt="" className="w-full h-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
            <Badge className="bg-[#1A4331] text-white border-none mb-3">{product.category}</Badge>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1c2826] leading-tight">
              {product.name}
            </h1>
            <p className="text-base text-[#4B5563] mt-3 leading-relaxed">{product.tagline}</p>
            <p className="text-xs text-[#7C9A82] font-semibold uppercase tracking-wider mt-3">
              {product.pack}
            </p>

            <div className="flex items-baseline gap-3 mt-5">
              <span data-testid="detail-price" className="text-4xl font-bold text-[#1A4331]">
                {formatINR(product.price)}
              </span>
              {hasDiscount && (
                <>
                  <span className="text-lg text-[#4B5563] line-through">{formatINR(product.mrp)}</span>
                  <Badge className="bg-[#C05C46] text-white border-none">
                    Save {formatINR(product.mrp - product.price)}
                  </Badge>
                </>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 my-7">
              <Button
                data-testid="detail-add-to-cart"
                variant="outline"
                onClick={handleAdd}
                className="rounded-full border-[#1A4331]/40 text-[#1A4331] hover:bg-[#1A4331] hover:text-white py-6"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Add to Cart
              </Button>
              <Button
                data-testid="detail-buy-now"
                onClick={() => buyNow(product.slug)}
                className="rounded-full bg-[#C05C46] hover:bg-[#a84d3a] text-white py-6"
              >
                <Zap className="w-4 h-4 mr-2" />
                Buy Now
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-7 text-center">
              {[
                [ShieldCheck, "100% Herbal"],
                [BadgeCheck, "Certified Quality"],
                [Truck, "Ships Across India"],
              ].map(([Icon, label]) => (
                <div key={label} className="rounded-xl bg-white border border-[#E5E0D8] p-3">
                  <Icon className="w-5 h-5 text-[#C05C46] mx-auto mb-1.5" />
                  <p className="text-[11px] font-semibold text-[#1c2826]">{label}</p>
                </div>
              ))}
            </div>

            <Accordion type="multiple" defaultValue={["why", "ingredients", "dosage"]}>
              <AccordionItem value="why">
                <AccordionTrigger data-testid="detail-accordion-why" className="text-[#1A4331] font-semibold">
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#C05C46]" /> Why to Use
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-2">
                    {product.benefits.map((b, i) => (
                      <li key={i} className="flex gap-2 text-sm text-[#4B5563] leading-relaxed">
                        <span className="text-[#7C9A82] mt-1">•</span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ingredients">
                <AccordionTrigger data-testid="detail-accordion-ingredients" className="text-[#1A4331] font-semibold">
                  <span className="flex items-center gap-2">
                    <Leaf className="w-4 h-4 text-[#C05C46]" /> Ingredients
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="space-y-1.5">
                    {product.ingredients.map((ing, i) => (
                      <li
                        key={i}
                        className="flex justify-between gap-3 text-sm text-[#4B5563] border-b border-dashed border-[#E5E0D8] pb-1.5"
                      >
                        <span>{ing.name}</span>
                        {ing.qty && <span className="text-[#1A4331] font-medium">{ing.qty}</span>}
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="dosage">
                <AccordionTrigger data-testid="detail-accordion-dosage" className="text-[#1A4331] font-semibold">
                  <span className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-[#C05C46]" /> How to Use
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm text-[#4B5563] leading-relaxed">{product.dosage}</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>
        </div>

        {relatedFill.length > 0 && (
          <div className="mt-20">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#1c2826] mb-8">
              You May Also Like
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {relatedFill.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
